/* global gtag */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const CookieConsentManager = () => {
  const { i18n } = useTranslation(); 
  const hasInitialized = useRef(false);
  const langCode = i18n.language;

  const normalizeLangCode = (code) => {
    // Map all English variants to 'en'
    if (code.startsWith('en_')) return 'en';
  
    return code;
  };


  useEffect(() => {
    const langCode = i18n.language;
    const normalizedLangCode = normalizeLangCode(i18n.language);

    if (!window.CookieConsent) {
      console.warn("CookieConsent script not available — possibly blocked (e.g. Brave)");
      return;
    }
  
    if (!hasInitialized.current) {
      initCookieConsent(normalizedLangCode);
      hasInitialized.current = true;
    } else {
      // Language changed? Update language only
      window.CookieConsent.setLanguage(normalizedLangCode);
    }
  }, [i18n.language]);


    // UpdateConsent:
    const updateConsent = (categories) => {
      window.dataLayer = window.dataLayer || [];
  
      if (categories.includes("analytics")) {
        gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'personalization_storage': 'granted',
        });
      } else {
        gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'personalization_storage': 'denied',
        });
      }
  
      if (categories.includes("marketing") || categories.includes("ads")) {
        gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
        });
      } else {
        gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
        });
      }
  
      window.dataLayer.push({ event: 'cookie_consent_updated' });
    }; 



  const initCookieConsent = (langCode) => {
    if (!window.CookieConsent) {
      console.warn('CookieConsent library not loaded — likely blocked by browser (e.g. Brave)');
      return;
    }

    const languageKey = langCode;

    const cssVariables = {
      "--cc-btn-primary-bg": "#5eba47",
      "--cc-btn-primary-border-color": "#5eba47",
      "--cc-btn-primary-hover-bg": "#448933",
      "--cc-btn-primary-hover-border-color": "#448933",
      "--cc-btn-primary-hover-color": "#fff",
    };

    // Apply the custom CSS variables to the document root
    Object.keys(cssVariables).forEach((variable) => {
      document.documentElement.style.setProperty(
        variable,
        cssVariables[variable]
      );
    });

    window.CookieConsent.run({
      cookie: { name: "cc_cookie" },
      guiOptions: {
        consentModal: {
          layout: "cloud inline",
          position: "bottom center",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: "box",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      onFirstConsent: () => {        
        console.log("onFirstAction fired");        
      },
      onConsent: ({cookie}) => {
        updateConsent(cookie.categories);
        console.log("onConsent fired ...");        
      },
      onChange: ({cookie}) => {
        updateConsent(cookie.categories);
        console.log("onChange fired ...");        
      },

      categories: {
        necessary: {
            readOnly: true
        },
        analytics: {},
        marketing: {}
    },    

      language: {
        autoDetect: "document",
        default: languageKey,
        translations: {
          // Unified English translation for all English variants
          en: {
            consentModal: {
              title: "This website uses cookies.",
              description:
                "This website uses cookies. We would therefore be pleased if you would allow us to place analytical cookies on your behalf.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage individual preferences",
              footer: `                
                <a href="/${langCode}/privacy-policy" target="_blank">Privacy Policy</a>
                <a href="/${langCode}/terms-of-use" target="_blank">Terms of use</a>
              `,
            },
            preferencesModal: {
              title: "Manage cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Accept current selection",
              closeIconLabel: "Close modal",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Your Privacy Choices",
                  description: `In this panel you can express some preferences related to the processing of your personal information. You may review and change expressed choices at any time by resurfacing this panel via the provided link. To deny your consent to the specific processing activities described below, switch the toggles to off or use the "Reject all" button and confirm you want to save your choices.`,
                },
                {
                  title: "Strictly Necessary",
                  description:
                    "These cookies are essential for the proper functioning of the website and cannot be disabled.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance and Analytics",
                  description:
                    "These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Targeting and Advertising",
                  description:
                    "These cookies are used to make advertising messages more relevant to you and your interests. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.",
                  linkedCategory: "ads",
                },
                {
                  title: "More information",
                  description: `For any queries in relation to our policy on cookies and your choices, please <a href="/${langCode}/contact">contact us</a>`,
                },
              ],
            },
          },

          // Slovenian (Slovenia)
          sl_si: {
            consentModal: {
              title: "Ta spletna stran uporablja piškotke.",
              description:
                "Ta spletna stran uporablja piškotke. Zato bi nam bilo všeč, če bi nam dovolili namestiti analitične piškotke v vašem imenu.",
              acceptAllBtn: "Sprejmi vse",
              acceptNecessaryBtn: "Zavrni vse",
              showPreferencesBtn: "Upravljanje posameznih nastavitev",
              footer: `                
                          <a href="/sl_si/politika-zasebnosti" target="_blank">Politika zasebnosti</a>
                          <a href="/sl_si/pogoji-uporabe" target="_blank">Pogoji uporabe</a>
                        `,
            },
            preferencesModal: {
              title: "Upravljanje nastavitev piškotkov",
              acceptAllBtn: "Sprejmi vse",
              acceptNecessaryBtn: "Zavrni vse",
              savePreferencesBtn: "Sprejmi trenutno izbiro",
              closeIconLabel: "Zapri",
              serviceCounterLabel: "Storitev|Storitve",
              sections: [
                {
                  title: "Vaše izbire zasebnosti",
                  description: `Na tej strani lahko izrazite svoje preference glede obdelave vaših osebnih podatkov. Svoje izbire lahko kadar koli pregledate in spremenite s ponovnim odpiranjem te plošče prek ponujene povezave. Če želite zavrniti soglasje za spodaj opisane specifične dejavnosti obdelave, preklopite stikala na položaj izklopa ali uporabite gumb "Zavrni vse" in potrdite, da želite shraniti svoje izbire.`,
                },
                {
                  title: "Nujno potrebni",
                  description:
                    "Ti piškotki so bistveni za pravilno delovanje spletne strani in jih ni mogoče onemogočiti.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Zmogljivostni in analitični",
                  description:
                    "Ti piškotki zbirajo informacije o tem, kako uporabljate našo spletno stran. Vsi podatki so anonimizirani in jih ni mogoče uporabiti za vašo identifikacijo.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Ciljanje in oglaševanje",
                  description:
                    "Ti piškotki se uporabljajo za to, da so oglasna sporočila bolj relevantna za vas in vaše interese. Namen je prikazati oglase, ki so pomembni in zanimivi za posameznega uporabnika ter s tem bolj dragoceni za založnike in zunanje oglaševalce.",
                  linkedCategory: "ads",
                },
                {
                  title: "Več informacij",
                  description:
                    'Za kakršna koli vprašanja v zvezi z našo politiko o piškotkih in vašimi izbirami vas prosimo, da nas <a href="/sl_si/kontakt">kontaktirate.</a>',
                },
              ],
            },
          },

          // German (Austria)
          de_at: {
            consentModal: {
              title: "Diese Website verwendet Cookies.",
              description:
                "Diese Website verwendet Cookies. Wir würden uns daher freuen, wenn Sie uns erlauben würden, analytische Cookies in Ihrem Namen zu platzieren.",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Alle ablehnen",
              showPreferencesBtn: "Individuelle Einstellungen verwalten",
              footer: `                
                <a href="/de_at/datenschutzrichtlinie" target="_blank">Datenschutzrichtlinie</a>
                <a href="/de_at/nutzungsbedingungen" target="_blank">Nutzungsbedingungen</a>
              `,
            },
            preferencesModal: {
              title: "Cookie-Einstellungen verwalten",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Alle ablehnen",
              savePreferencesBtn: "Aktuelle Auswahl akzeptieren",
              closeIconLabel: "Schließen",
              serviceCounterLabel: "Dienst|Dienste",
              sections: [
                {
                  title: "Ihre Datenschutzoptionen",
                  description: `In diesem Bereich können Sie einige Präferenzen bezüglich der Verarbeitung Ihrer personenbezogenen Daten angeben. Sie können die angegebenen Präferenzen jederzeit überprüfen und ändern, indem Sie diesen Bereich über den bereitgestellten Link erneut aufrufen. Um Ihre Einwilligung zu den unten beschriebenen spezifischen Verarbeitungsaktivitäten zu verweigern, schalten Sie die Schalter auf "Aus" oder verwenden Sie die Schaltfläche "Alle ablehnen" und bestätigen Sie, dass Sie Ihre Auswahl speichern möchten.`,
                },
                {
                  title: "Unbedingt notwendig",
                  description:
                    "Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich und können nicht deaktiviert werden.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Leistung und Analyse",
                  description:
                    "Diese Cookies sammeln Informationen darüber, wie Sie unsere Website nutzen. Alle Daten werden anonymisiert und können nicht zur Identifizierung Ihrer Person verwendet werden.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Targeting und Werbung",
                  description:
                    "Diese Cookies werden verwendet, um Werbebotschaften für Sie und Ihre Interessen relevanter zu gestalten. Die Absicht ist, Anzeigen zu schalten, die für den einzelnen Benutzer relevant und ansprechend sind und damit für Verlage und Drittanbieter von Werbung wertvoller sind.",
                  linkedCategory: "ads",
                },
                {
                  title: "Weitere Informationen",
                  description:
                    'Bei Fragen zu unserer Cookie-Richtlinie und Ihren Auswahlmöglichkeiten <a href="/de_at/kontakt">kontaktieren Sie uns bitte</a>',
                },
              ],
            },
          },

          // German (Germany)
          de_de: {
            consentModal: {
              title: "Diese Website verwendet Cookies.",
              description:
                "Diese Website verwendet Cookies. Wir würden uns daher freuen, wenn Sie uns erlauben würden, analytische Cookies in Ihrem Namen zu platzieren.",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Alle ablehnen",
              showPreferencesBtn: "Individuelle Einstellungen verwalten",
              footer: `                
                <a href="/de_de/datenschutzeklarung" target="_blank">Datenschutzrichtlinie</a>
                <a href="/de_de/nutzungsbedingungen" target="_blank">Nutzungsbedingungen</a>
              `,
            },
            preferencesModal: {
              title: "Cookie-Einstellungen verwalten",
              acceptAllBtn: "Alle akzeptieren",
              acceptNecessaryBtn: "Alle ablehnen",
              savePreferencesBtn: "Aktuelle Auswahl akzeptieren",
              closeIconLabel: "Schließen",
              serviceCounterLabel: "Dienst|Dienste",
              sections: [
                {
                  title: "Ihre Datenschutzoptionen",
                  description: `In diesem Bereich können Sie einige Präferenzen bezüglich der Verarbeitung Ihrer personenbezogenen Daten angeben. Sie können die angegebenen Präferenzen jederzeit überprüfen und ändern, indem Sie diesen Bereich über den bereitgestellten Link erneut aufrufen. Um Ihre Einwilligung zu den unten beschriebenen spezifischen Verarbeitungsaktivitäten zu verweigern, schalten Sie die Schalter auf "Aus" oder verwenden Sie die Schaltfläche "Alle ablehnen" und bestätigen Sie, dass Sie Ihre Auswahl speichern möchten.`,
                },
                {
                  title: "Unbedingt notwendig",
                  description:
                    "Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich und können nicht deaktiviert werden.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Leistung und Analyse",
                  description:
                    "Diese Cookies sammeln Informationen darüber, wie Sie unsere Website nutzen. Alle Daten werden anonymisiert und können nicht zur Identifizierung Ihrer Person verwendet werden.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Targeting und Werbung",
                  description:
                    "Diese Cookies werden verwendet, um Werbebotschaften für Sie und Ihre Interessen relevanter zu gestalten. Die Absicht ist, Anzeigen zu schalten, die für den einzelnen Benutzer relevant und ansprechend sind und damit für Verlage und Drittanbieter von Werbung wertvoller sind.",
                  linkedCategory: "ads",
                },
                {
                  title: "Weitere Informationen",
                  description:
                    'Bei Fragen zu unserer Cookie-Richtlinie und Ihren Auswahlmöglichkeiten <a href="/de_de/kontakt">kontaktieren Sie uns bitte</a>',
                },
              ],
            },
          },

          // Polish (Poland)
          pl_pl: {
            consentModal: {
              title: "Ta strona używa plików cookie.",
              description:
                "Ta strona używa plików cookie. Bylibyśmy zatem zadowoleni, gdyby zezwolili Państwo na umieszczenie analitycznych plików cookie w Państwa imieniu.",
              acceptAllBtn: "Akceptuj wszystkie",
              acceptNecessaryBtn: "Odrzuć wszystkie",
              showPreferencesBtn: "Zarządzaj indywidualnymi preferencjami",
              footer: `                
                <a href="/pl_pl/polityka-prywatnosci" target="_blank">Polityka prywatności</a>
                <a href="/pl_pl/warunki-korzystania" target="_blank">Warunki korzystania</a>
              `,
            },
            preferencesModal: {
              title: "Zarządzaj preferencjami plików cookie",
              acceptAllBtn: "Akceptuj wszystkie",
              acceptNecessaryBtn: "Odrzuć wszystkie",
              savePreferencesBtn: "Akceptuj bieżący wybór",
              closeIconLabel: "Zamknij",
              serviceCounterLabel: "Usługa|Usługi",
              sections: [
                {
                  title: "Twoje wybory dotyczące prywatności",
                  description: `W tym panelu możesz wyrazić swoje preferencje związane z przetwarzaniem Twoich danych osobowych. Możesz przeglądać i zmieniać wyrażone wybory w dowolnym momencie, ponownie otwierając ten panel za pomocą dostarczonego linku. Aby odmówić zgody na określone czynności przetwarzania opisane poniżej, przełącz przełączniki na pozycję wyłączoną lub użyj przycisku "Odrzuć wszystkie" i potwierdź, że chcesz zapisać swoje wybory.`,
                },
                {
                  title: "Niezbędne",
                  description:
                    "Te pliki cookie są niezbędne do prawidłowego funkcjonowania strony internetowej i nie mogą być wyłączone.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Wydajność i analityka",
                  description:
                    "Te pliki cookie gromadzą informacje o tym, jak korzystasz z naszej strony internetowej. Wszystkie dane są zanonimizowane i nie mogą zostać użyte do identyfikacji Twojej osoby.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Targetowanie i reklama",
                  description:
                    "Te pliki cookie są używane do tego, aby komunikaty reklamowe były bardziej odpowiednie dla Ciebie i Twoich zainteresowań. Celem jest wyświetlanie reklam, które są istotne i angażujące dla indywidualnego użytkownika, a tym samym bardziej wartościowe dla wydawców i zewnętrznych reklamodawców.",
                  linkedCategory: "ads",
                },
                {
                  title: "Więcej informacji",
                  description:
                    'W przypadku jakichkolwiek pytań dotyczących naszej polityki plików cookie i Twoich wyborów, prosimy o <a href="/pl_pl/kontakt">kontakt z nami</a>',
                },
              ],
            },
          },

          // Spanish (Spain)
          es_es: {
            consentModal: {
              title: "Este sitio web utiliza cookies.",
              description:
                "Este sitio web utiliza cookies. Por lo tanto, estaríamos complacidos si nos permitiera colocar cookies analíticas en su nombre.",
              acceptAllBtn: "Aceptar todo",
              acceptNecessaryBtn: "Rechazar todo",
              showPreferencesBtn: "Gestionar preferencias individuales",
              footer: `                
                <a href="/es_es/politica-de-privacidad" target="_blank">Política de privacidad</a>
                <a href="/es_es/condiciones-de-uso" target="_blank">Condiciones de uso</a>
              `,
            },
            preferencesModal: {
              title: "Gestionar preferencias de cookies",
              acceptAllBtn: "Aceptar todo",
              acceptNecessaryBtn: "Rechazar todo",
              savePreferencesBtn: "Aceptar selección actual",
              closeIconLabel: "Cerrar",
              serviceCounterLabel: "Servicio|Servicios",
              sections: [
                {
                  title: "Sus opciones de privacidad",
                  description: `En este panel puede expresar algunas preferencias relacionadas con el procesamiento de su información personal. Puede revisar y cambiar las opciones expresadas en cualquier momento volviendo a este panel a través del enlace proporcionado. Para denegar su consentimiento a las actividades específicas de procesamiento descritas a continuación, cambie los interruptores a la posición de apagado o utilice el botón "Rechazar todo" y confirme que desea guardar sus elecciones.`,
                },
                {
                  title: "Estrictamente necesarias",
                  description:
                    "Estas cookies son esenciales para el correcto funcionamiento del sitio web y no pueden ser desactivadas.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Rendimiento y analítica",
                  description:
                    "Estas cookies recopilan información sobre cómo utiliza nuestro sitio web. Todos los datos son anonimizados y no pueden ser utilizados para identificarle.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Segmentación y publicidad",
                  description:
                    "Estas cookies se utilizan para hacer que los mensajes publicitarios sean más relevantes para usted y sus intereses. La intención es mostrar anuncios que sean relevantes y atractivos para el usuario individual y, por lo tanto, más valiosos para los editores y anunciantes externos.",
                  linkedCategory: "ads",
                },
                {
                  title: "Más información",
                  description:
                    'Para cualquier consulta relacionada con nuestra política de cookies y sus elecciones, por favor <a href="/es_es/contacto">contáctenos</a>',
                },
              ],
            },
          },

          // Portuguese (Portugal)
          pt_pt: {
            consentModal: {
              title: "Este site utiliza cookies.",
              description:
                "Este site utiliza cookies. Ficaríamos satisfeitos se você nos permitisse colocar cookies analíticos em seu nome.",
              acceptAllBtn: "Aceitar tudo",
              acceptNecessaryBtn: "Rejeitar tudo",
              showPreferencesBtn: "Gerir preferências individuais",
              footer: `                
                <a href="/pt_pt/politica-de-privacidade" target="_blank">Política de Privacidade</a>
                <a href="/pt_pt/termos-de-utilizacao" target="_blank">Termos de Utilização</a>
              `,
            },
            preferencesModal: {
              title: "Gerir preferências de cookies",
              acceptAllBtn: "Aceitar tudo",
              acceptNecessaryBtn: "Rejeitar tudo",
              savePreferencesBtn: "Aceitar seleção atual",
              closeIconLabel: "Fechar",
              serviceCounterLabel: "Serviço|Serviços",
              sections: [
                {
                  title: "Suas escolhas de privacidade",
                  description: `Neste painel, você pode expressar algumas preferências relacionadas ao processamento de suas informações pessoais. Você pode revisar e alterar as escolhas expressas a qualquer momento, reacessando este painel através do link fornecido. Para negar seu consentimento às atividades específicas de processamento descritas abaixo, desligue os interruptores ou use o botão "Rejeitar tudo" e confirme que deseja salvar suas escolhas.`,
                },
                {
                  title: "Estritamente necessários",
                  description:
                    "Estes cookies são essenciais para o funcionamento adequado do site e não podem ser desativados.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Desempenho e análise",
                  description:
                    "Estes cookies coletam informações sobre como você usa nosso site. Todos os dados são anonimizados e não podem ser usados para identificá-lo.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Segmentação e publicidade",
                  description:
                    "Estes cookies são usados para tornar as mensagens publicitárias mais relevantes para você e seus interesses. A intenção é exibir anúncios que sejam relevantes e envolventes para o usuário individual e, portanto, mais valiosos para editores e anunciantes terceirizados.",
                  linkedCategory: "ads",
                },
                {
                  title: "Mais informações",
                  description:
                    'Para qualquer dúvida relacionada à nossa política de cookies e suas escolhas, por favor <a href="/pt_pt/contacto">entre em contato conosco</a>',
                },
              ],
            },
          },

          hr_hr: {
            consentModal: {
              title: "Ova web stranica koristi kolačiće.",
              description:
                "Ova web stranica koristi kolačiće. Stoga ćemo biti zahvalni ako nam dopustite postavljanje analitičkih kolačića u vaše ime.",
              acceptAllBtn: "Prihvati sve",
              acceptNecessaryBtn: "Odbij sve",
              showPreferencesBtn: "Upravljanje pojedinačnim preferencijama",
              footer: `                
                <a href="/hr_hr/politika-privatnosti" target="_blank">Politika privatnosti</a>
                <a href="/hr_hr/uvjeti-koristenja" target="_blank">Uvjeti korištenja</a>
              `,
            },
            preferencesModal: {
              title: "Upravljanje preferencijama kolačića",
              acceptAllBtn: "Prihvati sve",
              acceptNecessaryBtn: "Odbij sve",
              savePreferencesBtn: "Prihvati trenutni odabir",
              closeIconLabel: "Zatvori modal",
              serviceCounterLabel: "Usluga|Usluge",
              sections: [
                {
                  title: "Vaši izbori privatnosti",
                  description: `U ovom panela možete izraziti neke preferencije vezane uz obradu vaših osobnih podataka. Svoje odabrane preferencije možete pregledati i promijeniti u bilo kojem trenutku ponovnim otvaranjem ovog panela putem ponuđene poveznice. Ako želite uskratiti svoj pristanak za određene aktivnosti obrade opisanih u nastavku, isključite prekidače ili upotrijebite gumb "Odbij sve" i potvrđujete da želite spremiti svoje odabire.`,
                },
                {
                  title: "Strogo potrebni",
                  description:
                    "Ovi kolačići su nužni za pravilno funkcioniranje web stranice i ne mogu se onemogućiti.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performanse i analitika",
                  description:
                    "Ovi kolačići prikupljaju informacije o tome kako koristite našu web stranicu. Svi podaci su anonimni i ne mogu se koristiti za vašu identifikaciju.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Ciljanje i oglašavanje",
                  description:
                    "Ovi kolačići se koriste kako bi oglasne poruke bile relevantnije za vas i vaša interesiranja. Cilj je prikazivati oglase koji su relevantni i zanimljivi za pojedinca, čime su vrijedniji za izdavače i vanjske oglašivače.",
                  linkedCategory: "ads",
                },
                {
                  title: "Više informacija",
                  description:
                    'Za bilo kakve upite u vezi s mojom politikom kolačića i vašim izborima, molimo <a href="/hr_hr/kontakt">kontaktirajte nas</a>',
                },
              ],
            },
          },
        },
      },
    });
  };

  return null;
};

export default CookieConsentManager;
